import React, {useContext, useEffect, useRef, useState} from 'react';
import {Modal, ScrollView, StyleSheet, useWindowDimensions} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import {useNavigation} from '@react-navigation/native';
import MainStyle from '../../../../../../../MainStyle';
import Btn from 'components/Btn';
import {StoreContext} from 'components/StoreContextProvider';
import Div from 'lib/Div';
import {type CardEntryValidationError, useCardEntryForm} from 'hooks/useCardEntryForm';
import {useCardTokenization} from 'hooks/useCardTokenization';
import P from 'lib/P';
import {buildCardSummaryLabelFromNumber} from 'utils/cardEntry';
import CardDetailsStep from './components/CardDetailsStep';
import DepositErrorStep from './components/DepositErrorStep';
import DepositReviewStep from './components/DepositReviewStep';
import DepositSuccessStep from './components/DepositSuccessStep';
import {
  buildSafeCardTokenizationErrorLog, cardTokenizationPreparationErrorMessage, isCardTokenizationPreparationError
} from '../../../../../../services/cardTokenization';
import {
  getCardDepositErrorCause, getCardDepositErrorInfo, isAmbiguousCardDepositError, submitCardDeposit
} from '../../../../../../services/cardDeposit';
import {buildAccountHolderName, formatDepositMoney, missingApiValue} from './helpers';
import {styles} from './styles';
import {AddCardInfoModalProps, AddCardStep, SummaryLine} from './types';

const depositCardValidationMessages: Record<CardEntryValidationError, string> = {
  CARD_NUMBER_REQUIRED: 'Card number is required.', CARD_NUMBER_INVALID: 'Enter a valid card number.',
  EXPIRY_REQUIRED: 'Expiration date is required to save a card.', EXPIRY_INVALID: 'Enter a valid expiry date.',
  CVC_REQUIRED: 'CVC is required.', CVC_INVALID: 'Enter a valid CVC.'
};

const AddCardInfoModal = ({visible, onClose, amountCents}: AddCardInfoModalProps) => {
  const Context = useContext(StoreContext);
  const {user} = Context;
  const navigation = useNavigation<any>();
  const {width} = useWindowDimensions();
  const isDesktopFrame = width >= 640;
  const depositAmountLabel = formatDepositMoney(amountCents / 100);
  const depositFeeLabel = missingApiValue;
  const depositTotalLabel = missingApiValue;
  const [step, setStep] = useState<AddCardStep>('card');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [formError, setFormError] = useState('');
  const [resultErrorMessage, setResultErrorMessage] = useState('');
  const [canRetryDeposit, setCanRetryDeposit] = useState(true);
  const [isSubmittingDeposit, setIsSubmittingDeposit] = useState(false);
  const [submittedCardSummaryLabel, setSubmittedCardSummaryLabel] = useState('');
  const [submittedNewBalanceLabel, setSubmittedNewBalanceLabel] = useState('');
  const {
    status: tokenizationStatus, errorMessage: tokenizationError, retry: retryTokenization, tokenizeCard
  } = useCardTokenization(visible);
  const {
    cardBrand, cardSummaryLabel, values: cardEntryValues, cardNumberInputProps, expiryInputProps, cvcInputProps,
    reset: resetCardEntryForm, clearSensitiveFields, validate: validateCardEntry
  } = useCardEntryForm({onChange: () => setFormError('')});
  const activeDepositSubmissionRef = useRef(false);
  const cardBrandLabel = cardBrand || 'Card';
  const reviewLines: SummaryLine[] = [
    {label: 'From', value: cardSummaryLabel}, {label: 'To', value: 'Evenplay Wallet'},
    {label: 'Amount', value: depositAmountLabel}, {label: 'Processing fee', value: depositFeeLabel}
  ];
  const reviewTotal: SummaryLine = {label: 'Total charged', value: depositTotalLabel};
  const successLines: SummaryLine[] = [
    {label: 'New balance', value: submittedNewBalanceLabel || missingApiValue},
    {label: 'From', value: submittedCardSummaryLabel || cardSummaryLabel}, {label: 'Charged', value: missingApiValue}
  ];
  useEffect(() => {
    setAccountHolderName(buildAccountHolderName(user));
  }, [user]);

  const resetModalState = () => {
    setStep('card');
    resetCardEntryForm();
    setSaveCard(false);
    setFormError('');
    setResultErrorMessage('');
    setCanRetryDeposit(true);
    setIsSubmittingDeposit(false);
    activeDepositSubmissionRef.current = false;
    setSubmittedCardSummaryLabel('');
    setSubmittedNewBalanceLabel('');
    setAccountHolderName(buildAccountHolderName(user));
  };

  const closeModal = () => {
    resetModalState();
    onClose();
  };

  const requestClose = () => {
    if (!isSubmittingDeposit) closeModal();
  };

  const goBack = () => {
    if (isSubmittingDeposit) return;
    if (step === 'card') closeModal();
    else if (step === 'success') setStep('review');
    else setStep('card');
  };

  const completeAndNavigate = (routeName: 'challenges' | 'TransactionHistory') => {
    closeModal();
    navigation.navigate(routeName);
  };

  const validateCardDetails = () => {
    const error = validateCardEntry({requireExpiry: saveCard});
    return error ? depositCardValidationMessages[error] : '';
  };

  const continueToReview = () => {
    const errorMessage = validateCardDetails();

    if (errorMessage) {
      setFormError(errorMessage);
      return;
    }

    if (tokenizationStatus !== 'ready') {
      setFormError(tokenizationError || cardTokenizationPreparationErrorMessage);
      return;
    }

    setFormError('');
    setStep('review');
  };

  const submitDeposit = async () => {
    if (activeDepositSubmissionRef.current || isSubmittingDeposit) return;

    const errorMessage = validateCardDetails();

    if (errorMessage) {
      setFormError(errorMessage);
      setStep('card');
      return;
    }

    if (user?.selfLimitationConfirmed) {
      setFormError('You will not be able to play or deposit money while the Self-Limitation is in effect.');
      setStep('card');
      return;
    }

    if (tokenizationStatus !== 'ready') {
      setFormError(tokenizationError || cardTokenizationPreparationErrorMessage);
      setStep('card');
      return;
    }

    activeDepositSubmissionRef.current = true;
    setIsSubmittingDeposit(true);
    setFormError('');
    setCanRetryDeposit(true);

    try {
      const {number, cvc, expiry} = cardEntryValues;
      const submittedSummary = buildCardSummaryLabelFromNumber(cardBrand, number);
      const {eProtectResponse} = await submitCardDeposit({
        amountCents, saveCard, cardNumber: number, cvc, expirationDate: expiry, tokenizeCard, clearSensitiveFields
      });
      const refreshedBalance = await Context.getBalance();
      const refreshedBalanceValue = typeof refreshedBalance === 'number' ? refreshedBalance : NaN;

      setSubmittedCardSummaryLabel(cardBrand ? submittedSummary : buildCardSummaryLabelFromNumber(eProtectResponse.type, number));
      setSubmittedNewBalanceLabel(Number.isFinite(refreshedBalanceValue) ? formatDepositMoney(refreshedBalanceValue / 100) : missingApiValue);
      resetCardEntryForm();
      setStep('success');
      Context.getUser().catch(err => {
        const errorInfo = getCardDepositErrorInfo(err);
        console.log('deposit balance refresh error', {message: errorInfo.serverMessage || errorInfo.message});
      });

    } catch (err) {
      const cause = getCardDepositErrorCause(err);

      if (isCardTokenizationPreparationError(cause)) {
        console.warn('deposit card tokenization preparation error', buildSafeCardTokenizationErrorLog(cause));
        setFormError(cardTokenizationPreparationErrorMessage);
        setStep('card');
        return;
      }

      const errorInfo = getCardDepositErrorInfo(err);
      const ambiguousDepositError = isAmbiguousCardDepositError(err);
      console.log('deposit submission error', {message: errorInfo.serverMessage || errorInfo.message});
      setResultErrorMessage(ambiguousDepositError ? 'We could not confirm whether your deposit was completed. Please check your balance or contact support before trying again.' : errorInfo.message);
      setCanRetryDeposit(!ambiguousDepositError);
      resetCardEntryForm();
      setStep('error');

    } finally {
      activeDepositSubmissionRef.current = false;
      setIsSubmittingDeposit(false);
    }
  };

  const retryCardTokenization = () => {
    setFormError('');
    retryTokenization();
  };

  const retryFromError = () => {
    setFormError('');
    setResultErrorMessage('');
    setCanRetryDeposit(true);
    setStep('card');
  };

  const title = step === 'card' ? 'Add Card' : step === 'review' ? 'Review Deposit' : step === 'success' ? 'Deposit Complete' : 'Submission Error';

  return (
    <Modal animationType="fade" transparent visible={visible} statusBarTranslucent={false} hardwareAccelerated={false}
           onRequestClose={requestClose}>
      <Div style={[MainStyle.fix_gradient_and_modal_error_in_landscape, MainStyle.flex1, MainStyle.bg_filter_modal]}>
        <Btn
          testID="add-card-info-modal-backdrop" style={StyleSheet.absoluteFill} onPress={requestClose}
          disabled={isSubmittingDeposit}
        />

        <Div flex={1} alignItems="center" pointerEvents="box-none">
          <Div testID="add-card-info-modal-frame" flex={1} w="100%" maxW={440} rounded={isDesktopFrame ? 36 : 0}
               overflow="hidden" bg={step === 'success' || step === 'error' ? '#1b3131' : '#0e2424'}
               borderWidth={isDesktopFrame ? 1 : 0} borderColor="rgba(235, 255, 247, 0.16)">
            {step === 'success' ? (
              <DepositSuccessStep
                depositAmountLabel={depositAmountLabel} successLines={successLines}
                onPlayNow={() => completeAndNavigate('challenges')}
                onViewTransaction={() => completeAndNavigate('TransactionHistory')}
              />
            ) : step === 'error' ? (
              <DepositErrorStep
                resultErrorMessage={resultErrorMessage} canTryAgain={canRetryDeposit} onTryAgain={retryFromError}
                onBackToAccount={closeModal}
              />
            ) : (
              <>
                <Div bg="#0e2424" px={8} pt={10} pb={8} zIndex={30}>
                  <Div row alignItems="center" justifyContent="space-between">
                    <Btn onPress={goBack} w={44} h={44} rounded={999} alignItems="center" justifyContent="center"
                         accessibilityRole="button" accessibilityLabel="Back">
                      <MaterialIcons name="arrow-back" size={24} color="#ffffff"/>
                    </Btn>

                    <Btn onPress={requestClose} w={44} h={44} rounded={999} alignItems="center" justifyContent="center"
                         disabled={isSubmittingDeposit} accessibilityRole="button" accessibilityLabel="Close">
                      <MaterialIcons name="close" size={24} color="#ffffff"/>
                    </Btn>
                  </Div>

                  <Div alignItems="center" mt={12} px={8}>
                    <P fontFamily="POI Aeronaut" fs={28} lh={33.6} ta="center" color="#ffffff">{title}</P>
                  </Div>
                </Div>

                <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}
                            showsVerticalScrollIndicator={false}>
                  {step === 'card' ? (
                    <CardDetailsStep
                      accountHolderName={accountHolderName} cardNumberInputProps={cardNumberInputProps}
                      expiryInputProps={expiryInputProps} cvcInputProps={cvcInputProps}
                      saveCard={saveCard} formError={formError} isSubmittingDeposit={isSubmittingDeposit}
                      tokenizationStatus={tokenizationStatus} tokenizationError={tokenizationError}
                      onSaveCardToggle={() => setSaveCard(!saveCard)} onContinue={continueToReview}
                      onCancel={closeModal} onRetryTokenization={retryCardTokenization}
                    />
                  ) : (
                    <DepositReviewStep
                      cardBrandLabel={cardBrandLabel} depositAmountLabel={depositAmountLabel} formError={formError}
                      isSubmittingDeposit={isSubmittingDeposit} reviewLines={reviewLines} reviewTotal={reviewTotal}
                      onSubmit={submitDeposit} onCancel={closeModal}
                    />
                  )}
                </ScrollView>
              </>
            )}
          </Div>
        </Div>

      </Div>
    </Modal>
  );
};

export default AddCardInfoModal;
